import { defineStore } from 'pinia';

import {
  CampaignRepository,
  CampaignDetailResponse,
  GrantConditionForm,
} from '@/repositories/generated';
import {
  AccountTypeKey,
  ActionTypeKey,
  DeepPartial,
  ExpiredPatternKey,
  TargetSelectionKey,
  UseStartPatternKey,
} from '@/types';
import { CampaignForm } from '@/validations';

type TCampaignState = {
  form: CampaignForm | undefined;
  displayFileName: string;
  initData: DeepPartial<CampaignForm> | undefined;
};

export const useCampaignStore = defineStore('campaign', {
  state: (): TCampaignState => ({
    form: undefined,
    displayFileName: '',
    initData: undefined,
  }),
  actions: {
    async register() {
      const { form } = this;

      if (!form) return;

      await CampaignRepository.register({ requestBody: this.toCampaignRegisterForm(form) });
    },
    toCampaignRegisterForm(form: CampaignForm) {
      return {
        publicName: form.publicName,
        name: form.name,
        remarks: form.remarks,
        campaignTarget: {
          actionType: form.campaignTarget.actionType as ActionTypeKey,
          houseFlg: form.campaignTarget.houseFlg,
          smartCodeFlg: form.campaignTarget.smartCodeFlg,
          startDateTime: form.campaignTarget.startDateTime.toISOString(),
          endDateTime: form.campaignTarget.endDateTime?.toISOString(),
          companyIds: form.campaignTarget.companies.map((company) => company.id),
          shopIds: form.campaignTarget.shops.map((shop) => shop.id),
          targetSelection: form.campaignTarget.targetSelection as TargetSelectionKey,
          walletServiceIds: form.campaignTarget.walletServices.map((walletService) => walletService.id),
          fileName: form.campaignTarget.fileName,
        },
        pointInfo: {
          accountType: form.pointInfo.accountType as AccountTypeKey,
          useStartPattern: form.pointInfo.useStartPattern as UseStartPatternKey,
          useStartAddValue: form.pointInfo.useStartAddValue,
          pointExpire: {
            expiredPattern: form.pointInfo.pointExpire.expiredPattern as ExpiredPatternKey,
            dayCount: form.pointInfo.pointExpire.dayCount,
            specifiedDate: form.pointInfo.pointExpire.specifiedDate?.toISOString(),
            baseMonth: form.pointInfo.pointExpire.baseMonth,
            monthCount: form.pointInfo.pointExpire.monthCount,
            yearCount: form.pointInfo.pointExpire.yearCount,
          },
          grantCondition: form.pointInfo.grantCondition as GrantConditionForm[],
        },
      };
    },
    setForm(form: CampaignForm) {
      const createForm = (_form: CampaignForm) => ({
        ..._form,
        campaignTarget: {
          ..._form.campaignTarget,
        },
        pointInfo: {
          ..._form.pointInfo,
          pointExpire: {
            ..._form.pointInfo.pointExpire,
          },
          grantCondition: _form.pointInfo.grantCondition.map((grantCondition) => ({ ...grantCondition })),
        },
      });

      this.form = createForm(form);
      this.initData = createForm(form);
    },
    setInitData(form?: CampaignDetailResponse) {
      const specifiedDate = form?.pointInfo.pointExpire.specifiedDate;
      const startDateTime = form?.campaignTarget.startDateTime;
      const revisedEndDateTime = form?.campaignTarget.revisedEndDateTime;
      const endDateTime = revisedEndDateTime ?? form?.campaignTarget.endDateTime;

      this.initData = {
        name: form?.name,
        publicName: form?.publicName,
        remarks: form?.remarks,
        pointInfo: {
          ...form?.pointInfo,
          pointExpire: {
            ...form?.pointInfo.pointExpire,
            specifiedDate: specifiedDate && new Date(specifiedDate),
          },
        },
        campaignTarget: {
          ...form?.campaignTarget,
          startDateTime: startDateTime && new Date(startDateTime),
          endDateTime: endDateTime && new Date(endDateTime),
          shops: [],
          companies: [],
        },
      };
    },
  },
});
